import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Paper, IconButton, Divider,
  FormGroup, FormControlLabel, Checkbox
} from "@mui/material";
import { fetchPermissions, createPermission, updatePermission, deletePermission } from "../services/ApiService";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const departments = ["Admin", "Sales", "Customer Support", "Production", "Store", "Technical", "Collection Recovery"];

const PermissionManager = () => {
  const [permissions, setPermissions] = useState([]);
  const [department, setDepartment] = useState("");
  const [permissionNames, setPermissionNames] = useState([]);
  const [description, setDescription] = useState("");
  const [editingId, setEditingId] = useState(null);

  const menuItems = [
    { text: "Home",                       path: "/" },
    { text: "Permission Manager",         path: "/manage-permissions" },
    { text: "Sales",                      path: "/sales-new-installation" },
    { text: "Technicians",                path: "/technicians" },
    { text: "Add Customer",               path: "/add-customer" },
    { text: "Add Vehicle",                path: "/add-truck" },
    { text: "Add Solution",               path: "/add-device" },
    { text: "Customer Support",           path: "/customer-support" },
    { text: "Production",                 path: "/production" },
    { text: "Store",                      path: "/store" },
    { text: "Technical",                  path: "/technical" },
    { text: "Collection Recovery",        path: "/collection-recovery" },
    { text: "Collection Recovery History",path: "/collection-recovery-history" },
  ];

  const permissionOptions = menuItems.map(item => item.text);

  const loadPermissions = async () => {
    try {
      const res = await fetchPermissions();
      setPermissions(res.data);
    } catch (err) {
      console.error("Error fetching permissions:", err);
    }
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  const handlePermissionToggle = (name) => {
    setPermissionNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      department,
      permission_name: permissionNames,
      description,
    };

    try {
      if (editingId) {
        await updatePermission(editingId, payload);
      } else {
        await createPermission(payload);
      }

      setDepartment("");
      setPermissionNames([]);
      setDescription("");
      setEditingId(null);
      loadPermissions();
    } catch (err) {
      console.error("Error saving permission:", err);
    }
  };

  const handleEdit = (perm) => {
    setDepartment(perm.department);
    setPermissionNames(Array.isArray(perm.permission_name) ? perm.permission_name : [perm.permission_name]);
    setDescription(perm.description);
    setEditingId(perm.id);
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const handleDeleteClick = (id) => { setDeleteTargetId(id); setDeleteConfirmOpen(true); };
  const confirmDelete = async () => {
    if (deleteTargetId !== null) {
      try {
        await deletePermission(deleteTargetId);
        loadPermissions();
      } catch (err) {
        console.error("Error deleting permission:", err);
      }
    }
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const groupedPermissions = departments.reduce((acc, dept) => {
    acc[dept] = permissions.filter(p => p.department === dept);
    return acc;
  }, {});

  return (
    <Box sx={{ padding: 12 }}>
      <Typography variant="h5" gutterBottom>Add or Edit Department Permission</Typography>

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={department}
            label="Department"
            onChange={(e) => setDepartment(e.target.value)}
            required
          >
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Permissions</Typography>
          <FormGroup row>
            {permissionOptions.map((name) => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    checked={permissionNames.includes(name)}
                    onChange={() => handlePermissionToggle(name)}
                  />
                }
                label={name}
                sx={{ width: "45%", marginBottom: 1 }}
              />
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Button type="submit" variant="contained" color="primary">
          {editingId ? "Update" : "Add"} Permission
        </Button>
      </form>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom>Department-wise Permissions</Typography>

      {departments.map((dept) => (
        <Box key={dept} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{dept}</Typography>
          {groupedPermissions[dept]?.length > 0 ? (
            groupedPermissions[dept].map((perm) => (
              <Paper key={perm.id} sx={{ p: 2, mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography><strong>{Array.isArray(perm.permission_name) ? perm.permission_name.join(", ") : perm.permission_name}</strong></Typography>
                  <Typography variant="body2">{perm.description}</Typography>
                </Box>
                <Box>
                  <IconButton color="primary" onClick={() => handleEdit(perm)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteClick(perm.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))
          ) : (
            <Typography variant="body2" sx={{ color: "#999" }}>No permissions found.</Typography>
          )}
        </Box>
      ))}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default PermissionManager;

